import React, { useEffect, useState } from 'react';
import { db } from '../../firebase';
import { collection, query, onSnapshot, doc, where, getDocs, deleteDoc } from 'firebase/firestore';
import Modal from 'react-modal';
import { FaTable, FaUsers, FaClock, FaCheckCircle, FaExclamationTriangle, FaSpinner, FaTimes, FaUtensils, FaEuroSign, FaUserTie, FaEye, FaFilter, FaSort, FaTrashAlt, FaExclamationCircle } from 'react-icons/fa';
import './GlobalView.css';
import TableSVG from '../../assets/table.svg';

const GlobalView = () => {
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableOrders, setTableOrders] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tableLimit, setTableLimit] = useState(20);
  const [loading, setLoading] = useState(true);
  const [modalLoading, setModalLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' ou 'list'
  const [sortBy, setSortBy] = useState('number'); // 'number', 'status', 'orders'
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'occupied', 'available'
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [tableToFree, setTableToFree] = useState(null);

  useEffect(() => {
    // Configuration de React Modal
    Modal.setAppElement('#root');
    
    setLoading(true);
    
    const unsubscribeSettings = onSnapshot(doc(db, 'settings', 'nb_table'), (docSnap) => {
      if (docSnap.exists()) {
        const limit = docSnap.data().value;
        setTableLimit(limit || 20);
      } else {
        console.error('Le document "nb_table" n\'existe pas dans la collection settings.');
      }
    });

    const clientsCollection = collection(db, 'clients');
    const q = query(clientsCollection);

    const unsubscribeClients = onSnapshot(q, (snapshot) => {
      const occupiedTables = new Set();
      const enAttenteTables = new Set();
      const tableOrderCounts = new Map();
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.table) {
          occupiedTables.add(data.table);
          if (data.paiement?.status === 'en_attente') {
            enAttenteTables.add(data.table);
          }
          
          // Compter les commandes pour cette table
          const commandesCollection = collection(db, `clients/${doc.id}/commande`);
          onSnapshot(commandesCollection, (commandesSnapshot) => {
            const activeOrders = commandesSnapshot.docs.filter(commandeDoc => {
              const commandeData = commandeDoc.data();
              return commandeData.statut && commandeData.statut !== 'completed';
            });
            
            tableOrderCounts.set(data.table, activeOrders.length);
            
            // Mettre à jour les tables avec le bon nombre de commandes
            setTables(prevTables => 
              prevTables.map(table => ({
                ...table,
                orderCount: tableOrderCounts.get(table.id) || 0
                // Ne pas modifier le statut occupied si la table est contrôlée manuellement
              }))
            );
          });
        }
      });
      
      const initialTables = Array.from({ length: tableLimit }, (_, i) => ({
        id: i + 1,
        occupied: occupiedTables.has(i + 1),
        enAttentePaiement: enAttenteTables.has(i + 1),
        orderCount: tableOrderCounts.get(i + 1) || 0,
      }));

      setTables(initialTables);
      setLoading(false);
    });

    return () => {
      unsubscribeSettings();
      unsubscribeClients();
    };
  }, [tableLimit]);

  const handleTableClick = (table) => {
    setSelectedTable(table.id);
    setModalLoading(true);
    fetchTableOrders(table.id);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTableOrders([]);
    setModalLoading(false);
  };

  const fetchTableOrders = async (tableNumber) => {
    try {
      const clientsCollection = collection(db, 'clients');
      const q = query(clientsCollection, where('table', '==', tableNumber));

      onSnapshot(q, (snapshot) => {
        if (!snapshot.empty) {
          const commandes = [];
          snapshot.forEach((clientDoc) => {
            const commandesCollection = collection(db, `clients/${clientDoc.id}/commande`);
            onSnapshot(commandesCollection, (commandesSnapshot) => {
              commandesSnapshot.forEach((commandeDoc) => {
                const commandeData = commandeDoc.data();
                commandes.push({
                  id: commandeDoc.id,
                  ...commandeData,
                  serverName: commandeData.serverName || 'Inconnu',
                });
              });
              setTableOrders(commandes);
              setModalLoading(false);
            });
          });
        } else {
          setTableOrders([]);
          setModalLoading(false);
        }
      });
    } catch (error) {
      console.error('Erreur lors de la récupération des commandes :', error);
      setModalLoading(false);
    }
  };

  const calculateTotal = () => {
    return tableOrders.reduce((total, commande) => {
      const commandeTotal = commande.items.reduce(
        (sum, item) => sum + item.prix * item.quantity,
        0
      );
      return total + commandeTotal;
    }, 0);
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Initialisée':
        return 'global-view-status-label global-view-status-initialized';
      case 'Accepté':
        return 'global-view-status-label global-view-status-accepted';
      case 'Terminé':
        return 'global-view-status-label global-view-status-completed';
      case 'Commande servi':
        return 'global-view-status-label global-view-status-served';
      default:
        return 'global-view-status-label global-view-status-default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Initialisée':
        return <FaClock />;
      case 'Accepté':
        return <FaCheckCircle />;
      case 'Terminé':
        return <FaUtensils />;
      case 'Commande servi':
        return <FaUsers />;
      default:
        return <FaExclamationTriangle />;
    }
  };

  const handleFreeTableClick = (tableNumber) => {
    setTableToFree(tableNumber);
    setShowConfirmModal(true);
  };

  const handleConfirmFreeTable = async () => {
    if (!tableToFree) return;

    try {
      setShowConfirmModal(false);
      setModalLoading(true);
      
      // Récupérer tous les clients de cette table
      const clientsCollection = collection(db, 'clients');
      const q = query(clientsCollection, where('table', '==', tableToFree));
      const clientsSnapshot = await getDocs(q);

      let deletedClients = 0;

      // Pour chaque client de cette table
      for (const clientDoc of clientsSnapshot.docs) {
        const clientId = clientDoc.id;
        
        // Supprimer toutes les sous-collections (commande)
        const commandeCollection = collection(db, `clients/${clientId}/commande`);
        const commandeSnapshot = await getDocs(commandeCollection);
        
        const deletePromises = commandeSnapshot.docs.map((doc) => deleteDoc(doc.ref));
        await Promise.all(deletePromises);

        // Supprimer le document principal du client
        await deleteDoc(clientDoc.ref);
        deletedClients++;
      }

      console.log(`Table ${tableToFree} libérée avec succès. ${deletedClients} client(s) supprimé(s).`);
      
      // Fermer la modal et rafraîchir
      setIsModalOpen(false);
      setTableOrders([]);
      setModalLoading(false);
      setTableToFree(null);
      
    } catch (error) {
      console.error('Erreur lors de la libération de la table:', error);
      alert('Une erreur est survenue lors de la libération de la table');
      setModalLoading(false);
      setTableToFree(null);
    }
  };

  const handleCancelFreeTable = () => {
    setShowConfirmModal(false);
    setTableToFree(null);
  };

  const getFilteredAndSortedTables = () => {
    let filteredTables = [...tables];
    
    // Filtrage
    if (filterStatus === 'occupied') {
      filteredTables = filteredTables.filter(table => table.occupied || table.orderCount > 0);
    } else if (filterStatus === 'available') {
      filteredTables = filteredTables.filter(table => !table.occupied && table.orderCount === 0);
    }
    
    // Tri
    switch (sortBy) {
      case 'status':
        filteredTables.sort((a, b) => {
          const aOccupied = a.occupied || a.orderCount > 0;
          const bOccupied = b.occupied || b.orderCount > 0;
          return bOccupied - aOccupied;
        });
        break;
      case 'orders':
        filteredTables.sort((a, b) => b.orderCount - a.orderCount);
        break;
      default: // 'number'
        filteredTables.sort((a, b) => a.id - b.id);
    }
    
    return filteredTables;
  };

  const getTableStats = () => {
    const occupied = tables.filter(table => table.occupied || table.orderCount > 0).length;
    const available = tables.length - occupied;
    const pendingPayment = tables.filter(table => table.enAttentePaiement).length;
    const totalOrders = tables.reduce((sum, table) => sum + table.orderCount, 0);
    
    return { occupied, available, pendingPayment, totalOrders };
  };


  const stats = getTableStats();
  const filteredTables = getFilteredAndSortedTables();

  if (loading) {
    return (
      <div className="global-view-container">
        <div className="global-view-loading-container">
          <FaSpinner className="global-view-loading-spinner" />
          <p className="global-view-loading-text">Chargement des tables...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="global-view-container">
      {/* Header */}
      <div className="global-view-header">
        <div className="global-view-header-content">
          <div className="global-view-title-section">
            <FaTable className="global-view-title-icon" />
            <div className="global-view-title-text">
              <h1 className="global-view-title">Vue Globale des Tables</h1>
              <p className="global-view-subtitle">Gestion et surveillance des tables en temps réel</p>
            </div>
          </div>
          
          {/* Stats */}
          <div className="global-view-stats">
            <div className="global-view-stat-card">
              <FaUsers className="global-view-stat-icon global-view-stat-occupied" />
              <div className="global-view-stat-content">
                <span className="global-view-stat-value">{stats.occupied}</span>
                <span className="global-view-stat-label">Occupées</span>
              </div>
            </div>
            <div className="global-view-stat-card">
              <FaCheckCircle className="global-view-stat-icon global-view-stat-available" />
              <div className="global-view-stat-content">
                <span className="global-view-stat-value">{stats.available}</span>
                <span className="global-view-stat-label">Disponibles</span>
              </div>
            </div>
            <div className="global-view-stat-card">
              <FaExclamationTriangle className="global-view-stat-icon global-view-stat-pending" />
              <div className="global-view-stat-content">
                <span className="global-view-stat-value">{stats.pendingPayment}</span>
                <span className="global-view-stat-label">Paiements en attente</span>
              </div>
            </div>
            <div className="global-view-stat-card">
              <FaUtensils className="global-view-stat-icon global-view-stat-orders" />
              <div className="global-view-stat-content">
                <span className="global-view-stat-value">{stats.totalOrders}</span>
                <span className="global-view-stat-label">Commandes actives</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="global-view-controls">
        <div className="global-view-controls-section">
          <div className="global-view-filters">
            <div className="global-view-filter-group">
              <FaFilter className="global-view-filter-icon" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="global-view-filter-select"
              >
                <option value="all">Toutes les tables</option>
                <option value="occupied">Tables occupées</option>
                <option value="available">Tables disponibles</option>
              </select>
            </div>
            
            <div className="global-view-filter-group">
              <FaSort className="global-view-filter-icon" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="global-view-filter-select"
              >
                <option value="number">Trier par numéro</option>
                <option value="status">Trier par statut</option>
                <option value="orders">Trier par commandes</option>
              </select>
            </div>
          </div>
          
          <div className="global-view-view-controls">
            <button
              onClick={() => setViewMode('grid')}
              className={`global-view-view-btn ${viewMode === 'grid' ? 'global-view-view-btn-active' : ''}`}
            >
              <FaTable />
              <span>Grille</span>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`global-view-view-btn ${viewMode === 'list' ? 'global-view-view-btn-active' : ''}`}
            >
              <FaEye />
              <span>Liste</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tables Grid/List */}
      <div className={`global-view-tables-container ${viewMode === 'list' ? 'global-view-tables-list' : 'global-view-tables-grid'}`}>
        {filteredTables.map(table => {
          const isOccupied = table.occupied || table.orderCount > 0;

          return (
            <div
              key={table.id}
              className={`global-view-table-card ${isOccupied ? 'global-view-table-occupied' : 'global-view-table-available'} ${table.enAttentePaiement ? 'global-view-table-pending-payment' : ''}`}
              onClick={() => handleTableClick(table)}
            >
              <div className="global-view-table-header">
                <div className="global-view-table-icon-container">
                  <img src={TableSVG} alt="Table" className="global-view-table-icon" />
              {table.enAttentePaiement && (
                    <div className="global-view-payment-badge" title="Paiement en attente">
                      <FaExclamationTriangle />
                    </div>
                  )}
                </div>
                <div className="global-view-table-info">
                  <h3 className="global-view-table-number">Table {table.id}</h3>
                  <span className={`global-view-table-status ${isOccupied ? 'global-view-status-occupied' : 'global-view-status-available'}`}>
                {isOccupied ? 'Occupée' : 'Disponible'}
              </span>
                </div>
              </div>
              
              <div className="global-view-table-details">
                <div className="global-view-table-stat">
                  <FaUtensils className="global-view-table-stat-icon" />
                  <span className="global-view-table-stat-text">{table.orderCount} commande{table.orderCount > 1 ? 's' : ''}</span>
                </div>
                <div className="global-view-table-actions">
                  {isOccupied && (
                    <div className="global-view-table-action">
                      <FaEye className="global-view-table-action-icon" />
                      <span className="global-view-table-action-text">Voir détails</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        contentLabel="Commandes de la Table"
        className="global-view-modal"
        overlayClassName="global-view-modal-overlay"
      >
        <div className="global-view-modal-content">
          <div className="global-view-modal-header">
            <div className="global-view-modal-title-section">
              <FaTable className="global-view-modal-icon" />
              <div className="global-view-modal-title-text">
                <h2 className="global-view-modal-title">Commandes - Table {selectedTable}</h2>
                <p className="global-view-modal-subtitle">Détails des commandes en cours</p>
              </div>
            </div>
            <button onClick={closeModal} className="global-view-modal-close">
              <FaTimes />
            </button>
          </div>
          
          <div className="global-view-modal-body">
            {modalLoading ? (
              <div className="global-view-modal-loading">
                <FaSpinner className="global-view-modal-loading-spinner" />
                <p className="global-view-modal-loading-text">Chargement des commandes...</p>
              </div>
            ) : tableOrders.length > 0 ? (
              <div className="global-view-modal-orders">
            {tableOrders.map((commande, index) => (
                  <div key={commande.id} className="global-view-order-card">
                    <div className="global-view-order-header">
                      <div className="global-view-order-info">
                        <h4 className="global-view-order-id">Commande #{commande.id.slice(-6)}</h4>
                        <div className="global-view-order-server">
                          <FaUserTie className="global-view-order-server-icon" />
                          <span className="global-view-order-server-name">{commande.serverName || 'Non assigné'}</span>
                        </div>
                      </div>
                      <div className={getStatusStyle(commande.statut)}>
                        {getStatusIcon(commande.statut)}
                        <span>{commande.statut}</span>
                      </div>
                    </div>
                    
                    <div className="global-view-order-items">
                      <h5 className="global-view-order-items-title">Articles commandés :</h5>
                  {commande.items.map((item, itemIndex) => (
                        <div key={itemIndex} className="global-view-order-item">
                          <div className="global-view-order-item-info">
                            <span className="global-view-order-item-name">{item.nom}</span>
                            <span className="global-view-order-item-quantity">x{item.quantity}</span>
                          </div>
                          <div className="global-view-order-item-price">
                            <span className="global-view-order-item-unit-price">{item.prix.toFixed(2)} €</span>
                            <span className="global-view-order-item-total">{(item.prix * item.quantity).toFixed(2)} €</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="global-view-order-total">
                      <FaEuroSign className="global-view-order-total-icon" />
                      <span className="global-view-order-total-label">Total commande :</span>
                      <span className="global-view-order-total-amount">
                        {commande.items.reduce((acc, item) => acc + item.prix * item.quantity, 0).toFixed(2)} €
                      </span>
                    </div>
                    
                    {index < tableOrders.length - 1 && <hr className="global-view-order-separator" />}
                  </div>
                ))}
                
                <div className="global-view-modal-summary">
                  <div className="global-view-summary-card">
                    <FaEuroSign className="global-view-summary-icon" />
                    <div className="global-view-summary-content">
                      <span className="global-view-summary-label">Total table {selectedTable}</span>
                      <span className="global-view-summary-amount">{calculateTotal().toFixed(2)} €</span>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => handleFreeTableClick(selectedTable)}
                    className="global-view-free-table-btn"
                    disabled={modalLoading}
                  >
                    <FaTrashAlt className="global-view-free-table-icon" />
                    <span>Libérer la table</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="global-view-modal-empty">
                <FaTable className="global-view-modal-empty-icon" />
                <h3 className="global-view-modal-empty-title">Aucune commande</h3>
                <p className="global-view-modal-empty-text">Cette table n'a actuellement aucune commande en cours.</p>
                
                <button 
                  onClick={() => handleFreeTableClick(selectedTable)}
                  className="global-view-free-table-btn"
                  disabled={modalLoading}
                >
                  <FaTrashAlt className="global-view-free-table-icon" />
                  <span>Libérer la table</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Confirmation Modal */}
      <Modal
        isOpen={showConfirmModal}
        onRequestClose={handleCancelFreeTable}
        contentLabel="Confirmation de libération"
        className="global-view-confirm-modal"
        overlayClassName="global-view-confirm-overlay"
      >
        <div className="global-view-confirm-content">
          <div className="global-view-confirm-icon-container">
            <FaExclamationCircle className="global-view-confirm-icon" />
          </div>
          
          <h2 className="global-view-confirm-title">Libérer la table {tableToFree} ?</h2>
          
          <p className="global-view-confirm-message">
            Cette action supprimera <strong>tous les clients</strong> associés à cette table.
            <br />
            <span className="global-view-confirm-warning">⚠️ Cette action est irréversible !</span>
          </p>
          
          <div className="global-view-confirm-actions">
            <button 
              onClick={handleCancelFreeTable}
              className="global-view-confirm-btn global-view-confirm-cancel"
            >
              <FaTimes />
              <span>Annuler</span>
            </button>
            <button 
              onClick={handleConfirmFreeTable}
              className="global-view-confirm-btn global-view-confirm-delete"
            >
              <FaTrashAlt />
              <span>Libérer la table</span>
            </button>
          </div>
        </div>
      </Modal>

    </div>
  );
};

export default GlobalView;
